/**
 * Centralized LinkedIn URL utility functions.
 * Use these helpers for all LinkedIn URL parsing across frontend and backend.
 *
 * @module linkedin-url-utils
 */

/**
 * Decodes URL-encoded input if needed (handles %XX encoding)
 * @internal
 */
function decodeIfNeeded(input: string): string {
  try {
    if (/%[0-9A-Fa-f]{2}/.test(input)) {
      return decodeURIComponent(input);
    }
  } catch {
    // Ignore decoding errors
  }
  return input;
}

/**
 * Extracts a URL path segment and decodes it.
 * Uses a broad match to capture percent-encoded characters (e.g. %C3%A9 for é),
 * then decodes the extracted segment individually.
 * Falls back to the raw segment if decoding fails.
 */
export function extractAndDecodePathSegment(url: string, type: string): string | null {
  const pattern = new RegExp(`linkedin\\.com\\/${type}\\/([^\\s/?#]+)`, 'i');
  const match = url.match(pattern);
  if (!match) return null;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
}

/**
 * Extracts LinkedIn username/identifier from ANY profile URL type.
 * This is the primary function for matching posts to channels.
 *
 * Supports all LinkedIn URL patterns:
 * - Personal: https://www.linkedin.com/in/john-doe → john-doe
 * - Company: https://www.linkedin.com/company/example-company → example-company
 * - School: https://www.linkedin.com/school/example-school/posts → example-school
 * - Showcase: https://www.linkedin.com/showcase/example → example
 *
 * @param profileUrl - The LinkedIn profile URL to parse
 * @returns The extracted username/identifier, or null if not found
 *
 * @example
 * extractLinkedInUsernameFromUrl('https://www.linkedin.com/company/google/') // 'google'
 * extractLinkedInUsernameFromUrl('https://www.linkedin.com/in/john-doe') // 'john-doe'
 * extractLinkedInUsernameFromUrl('https://www.linkedin.com/school/mit/posts') // 'mit'
 */
export function extractLinkedInUsernameFromUrl(profileUrl: string | undefined | null): string | null {
  if (!profileUrl) return null;

  // Try each URL type with broad path segment matching to handle percent-encoded chars
  for (const type of ['company', 'school', 'showcase', 'in']) {
    const identifier = extractAndDecodePathSegment(profileUrl, type);
    if (identifier) {
      return identifier.toLowerCase();
    }
  }

  return null;
}

/**
 * Extracts LinkedIn identifier from user input (URL or plain identifier).
 * Use this for form validation and user input processing.
 *
 * Handles multiple input formats:
 * - Full URL: https://www.linkedin.com/in/john-doe → john-doe
 * - Company URL (if allowCompany=true): https://linkedin.com/company/google → google
 * - Username with @: @john-doe → john-doe
 * - Plain identifier: john-doe → john-doe
 * - URL-encoded: linkedin.com/in/john%2Ddoe → john-doe
 *
 * @param input - The user input (URL or identifier)
 * @param allowCompany - If true, also matches company/school/showcase URLs
 * @returns The extracted identifier
 *
 * @example
 * extractLinkedInIdentifierFromInput('https://linkedin.com/in/john-doe') // 'john-doe'
 * extractLinkedInIdentifierFromInput('@john-doe') // 'john-doe'
 * extractLinkedInIdentifierFromInput('john-doe') // 'john-doe'
 * extractLinkedInIdentifierFromInput('https://linkedin.com/company/google', true) // 'google'
 */
export function extractLinkedInIdentifierFromInput(input: string, allowCompany: boolean = false): string {
  if (!input) return '';

  // Try broad path-segment extraction first (handles percent-encoded chars like %C3%A9 for é)
  const profileId = extractAndDecodePathSegment(input, 'in');
  if (profileId) {
    return profileId.toLowerCase();
  }

  if (allowCompany) {
    for (const type of ['company', 'showcase', 'school']) {
      const companyId = extractAndDecodePathSegment(input, type);
      if (companyId) {
        return companyId.toLowerCase();
      }
    }
  }

  const decodedInput = decodeIfNeeded(input);

  // Remove any leading @ symbol
  if (decodedInput.startsWith('@')) {
    return decodedInput.substring(1).trim().toLowerCase();
  }

  // Otherwise assume it's already the public identifier - normalize to lowercase
  return decodedInput.trim().toLowerCase();
}

/**
 * Validates if a LinkedIn public identifier is a real vanity URL
 * and not a member ID (which starts with "ACo").
 *
 * LinkedIn member IDs have the format: ACoAAXXXXXX...
 * Valid public identifiers look like: jean-dupont, john-doe-123
 *
 * @param publicIdentifier - The public identifier to validate
 * @returns true if it's a valid public identifier, false if it's a member ID or empty
 */
export function isValidLinkedInPublicIdentifier(publicIdentifier: string | undefined | null): boolean {
  if (!publicIdentifier) {
    return false;
  }

  // LinkedIn member IDs start with "ACo" (base64-encoded format)
  if (publicIdentifier.startsWith('ACo')) {
    return false;
  }

  return true;
}

/**
 * Validates if a string is a valid LinkedIn member ID in ACoAAxxxx format.
 * This format is required for urn:li:fsd_profile: URNs used by LinkedIn APIs (e.g., follow API).
 *
 * @param memberId - The member ID to validate
 * @returns true if it's a valid ACoAAxxxx format member ID
 */
export function isValidLinkedinMemberId(memberId: string | undefined | null): memberId is string {
  return !!memberId && memberId.startsWith('ACoAA');
}

/**
 * Validates if a string is a valid LinkedIn company ID (numeric format).
 * Company IDs are purely numeric (e.g., "110202713").
 *
 * @param companyId - The company ID to validate
 * @returns true if it's a valid numeric company ID
 */
export function isValidLinkedinCompanyId(companyId: string | undefined | null): companyId is string {
  return !!companyId && /^\d+$/.test(companyId);
}

/**
 * Builds the correct LinkedIn profile URN for a personal account.
 * Format: urn:li:fsd_profile:ACoAAxxxx
 *
 * @param memberId - The LinkedIn member ID (ACoAAxxxx format)
 * @returns The fsd_profile URN, or undefined if memberId is invalid
 */
export function buildLinkedInProfileUrn(memberId: string | undefined | null): string | undefined {
  if (!isValidLinkedinMemberId(memberId)) {
    return undefined;
  }
  return `urn:li:fsd_profile:${memberId}`;
}

/**
 * Builds the correct LinkedIn company URN.
 * Format: urn:li:fsd_company:12345
 *
 * @param companyId - The LinkedIn company ID (numeric format)
 * @returns The fsd_company URN, or undefined if companyId is invalid
 */
export function buildLinkedInCompanyUrn(companyId: string | undefined | null): string | undefined {
  if (!isValidLinkedinCompanyId(companyId)) {
    return undefined;
  }
  return `urn:li:fsd_company:${companyId}`;
}

/**
 * Extracts the numeric company ID from various URN or ID formats.
 * Handles:
 * - Plain numeric ID: "110202713" → "110202713"
 * - Full URN: "urn:li:company:110202713" → "110202713"
 * - FSD URN: "urn:li:fsd_company:110202713" → "110202713"
 *
 * @param urnOrId - The URN or ID to parse
 * @returns The extracted numeric company ID, or undefined if not found
 */
export function extractCompanyIdFromUrn(urnOrId: string | undefined | null): string | undefined {
  if (!urnOrId) return undefined;

  // If it's already a plain numeric ID
  if (/^\d+$/.test(urnOrId)) {
    return urnOrId;
  }

  // Try to extract from URN format (urn:li:company:xxx or urn:li:fsd_company:xxx)
  const match = urnOrId.match(/urn:li:(?:fsd_)?company:(\d+)/);
  if (match) {
    return match[1];
  }

  return undefined;
}

/**
 * Returns the public identifier if valid, otherwise returns undefined.
 * Use this to safely get a public identifier for storage.
 * Also decodes URL-encoded characters.
 *
 * @param publicIdentifier - The public identifier to sanitize
 * @returns The public identifier if valid, undefined otherwise
 */
export function sanitizeLinkedInPublicIdentifier(publicIdentifier: string | undefined | null): string | undefined {
  if (isValidLinkedInPublicIdentifier(publicIdentifier)) {
    // Always return in lowercase for consistent matching
    return decodeIfNeeded(publicIdentifier!).toLowerCase();
  }
  return undefined;
}

/**
 * Builds a LinkedIn profile URL only if the public identifier is valid.
 *
 * @param publicIdentifier - The public identifier
 * @returns The profile URL if valid, undefined otherwise
 */
export function buildLinkedInProfileUrl(publicIdentifier: string | undefined | null): string | undefined {
  const sanitized = sanitizeLinkedInPublicIdentifier(publicIdentifier);
  if (sanitized) {
    return `https://linkedin.com/in/${sanitized}`;
  }
  return undefined;
}

/**
 * The publisher's slug in a linkedin.com/posts/<slug>_... URL, decoded. Other URL forms don't
 * carry one, /posts/activity-<id>-<hash> included: its '_' belongs to the hash.
 */
export function extractLinkedInAuthorSlugFromPostUrl(url: string | undefined | null): string | undefined {
  const raw = url?.match(/linkedin\.com\/posts\/([^/?#_]+)_/i)?.[1];
  if (!raw || /^activity(-|$)/i.test(raw)) return undefined;
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
}

/**
 * Normalizes a LinkedIn post URL by stripping query params and hash fragments.
 * Removes tracking params like utm_source, rcm, etc. that LinkedIn appends
 * on shares (e.g. ?utm_source=social_share_send&rcm=...).
 *
 * @param url - The LinkedIn post URL to normalize
 * @returns The URL without query/fragment, or the original value if parsing fails
 *
 * @example
 * normalizeLinkedInPostUrl('https://www.linkedin.com/posts/foo_activity-123-abc?utm_source=social_share_send&rcm=X')
 * // 'https://www.linkedin.com/posts/foo_activity-123-abc'
 */
export function normalizeLinkedInPostUrl<T extends string | undefined | null>(url: T): T {
  if (!url) return url;
  // A tracking link is nothing but its query string: stripping it leaves a bare
  // /ls/click that resolves to nothing. Hand it back untouched — the resolver
  // normalizes the LinkedIn URL it lands on.
  if (isPodawaaEmailTrackingUrl(url)) return url;
  try {
    const parsed = new URL(url);
    parsed.search = '';
    parsed.hash = '';
    return parsed.toString() as T;
  } catch {
    // Fallback: strip everything after ? or #
    return url.replace(/[?#].*$/, '') as T;
  }
}

/**
 * Whether a user-provided URL can identify a LinkedIn feed post.
 * Short lnkd.in links are resolved server-side before scraping.
 */
export function isSupportedLinkedInPostUrl(url: string | undefined | null): boolean {
  if (!url) return false;

  try {
    const parsed = new URL(url);
    if (!['http:', 'https:'].includes(parsed.protocol)) return false;

    const hostname = parsed.hostname.toLowerCase();
    if (hostname === 'lnkd.in') {
      return parsed.protocol === 'https:' && (!parsed.port || parsed.port === '443') && parsed.pathname !== '/';
    }

    // Our own email click-tracking links: accepted here, resolved server-side.
    // Rejecting them told customers their own post link was invalid (#451).
    if (isPodawaaEmailTrackingUrl(url)) return true;

    const isLinkedInHost = hostname === 'linkedin.com' || hostname.endsWith('.linkedin.com');
    return isLinkedInHost && (
      parsed.pathname.startsWith('/posts/') ||
      parsed.pathname.startsWith('/feed/update/')
    );
  } catch {
    return false;
  }
}

export function isLinkedInShortUrl(url: string | undefined | null): boolean {
  if (!url) return false;

  try {
    const parsed = new URL(url);
    return parsed.protocol === 'https:' &&
      parsed.hostname.toLowerCase() === 'lnkd.in' &&
      (!parsed.port || parsed.port === '443') &&
      parsed.pathname !== '/';
  } catch {
    return false;
  }
}

/**
 * A click-tracking link from our own transactional emails.
 *
 * SendGrid rewrites every link we mail as `https://url<digits>.podawaa.com/ls/click?upn=…`,
 * the post URL living inside the query string. Users click one of those in an
 * email, land on a page whose address bar still shows the tracking link, copy
 * it, and paste it into Boost-from-URL — three customers in 25 minutes on
 * 2026-08-21 (#451). The link is ours and resolves to a valid post, so the flow
 * must follow it rather than call it an invalid LinkedIn URL.
 */
export function isPodawaaEmailTrackingUrl(url: string | undefined | null): boolean {
  if (!url) return false;

  try {
    const parsed = new URL(url);
    if (!['http:', 'https:'].includes(parsed.protocol)) return false;
    const hostname = parsed.hostname.toLowerCase();
    // The subdomain carries SendGrid's account digits and changes over time.
    return /^url\d+\.podawaa\.com$/.test(hostname) && parsed.pathname.startsWith('/ls/click');
  } catch {
    return false;
  }
}

/**
 * Whether the URL has to be followed before it can say anything about a post.
 *
 * Both cases hide the real post behind a redirect: LinkedIn's own `lnkd.in`
 * shortener and our email click-tracking links. Anything else is taken at face
 * value — the resolver only ever fetches hosts listed here.
 */
export function isResolvableRedirectUrl(url: string | undefined | null): boolean {
  return isLinkedInShortUrl(url) || isPodawaaEmailTrackingUrl(url);
}

/**
 * Why a LinkedIn URL cannot be boosted, or `null` when it can.
 *
 * The messages are shown to the user verbatim, so each one says what the URL is
 * and what to paste instead — "invalid URL" on a link the person copied straight
 * from LinkedIn reads as a Podawaa bug.
 */
export function describeUnboostableLinkedInPostUrl(url: string | undefined | null): string | null {
  if (!url) return 'Invalid LinkedIn post URL';

  if (url.includes('linkedin.com/pulse/')) {
    return 'LinkedIn articles (Pulse) cannot be boosted. Only regular feed posts (linkedin.com/posts/ or linkedin.com/feed/update/) are supported.';
  }

  // Group posts live behind the group's membership wall: only members can see
  // them, and a pool account that is not in the group gets a 404 from the
  // reaction API. Accepting one produced a boost that spent three injection
  // attempts, cancelled itself, and mailed the author "post not found" — for a
  // post they were looking at (#290). Worse, the retries kept re-picking pool
  // accounts and drained the English reaction pool for four days (#371).
  if (/urn(:|%3A)li(:|%3A)groupPost/i.test(url)) {
    return 'Posts published inside a LinkedIn group cannot be boosted — only members of the group can see them. Publish the post on your feed to boost it.';
  }

  if (!url.includes('linkedin.com/')) {
    return 'Invalid LinkedIn post URL';
  }

  return null;
}

/**
 * Detects the type of LinkedIn URL (personal, company, school, showcase).
 *
 * @param url - The LinkedIn URL to analyze
 * @returns Object with identifier and type
 *
 * @example
 * detectLinkedInUrlType('https://linkedin.com/company/google') // { identifier: 'google', type: 'company' }
 * detectLinkedInUrlType('https://linkedin.com/in/john-doe') // { identifier: 'john-doe', type: 'personal' }
 * detectLinkedInUrlType('https://linkedin.com/school/mit') // { identifier: 'mit', type: 'school' }
 */
export function detectLinkedInUrlType(url: string): { identifier: string; type: 'personal' | 'company' | 'showcase' | 'school' | null } {
  if (!url) return { identifier: '', type: null };

  const typeMap: Array<{ urlType: string; resultType: 'school' | 'showcase' | 'company' | 'personal' }> = [
    { urlType: 'school', resultType: 'school' },
    { urlType: 'showcase', resultType: 'showcase' },
    { urlType: 'company', resultType: 'company' },
    { urlType: 'in', resultType: 'personal' },
  ];

  for (const { urlType, resultType } of typeMap) {
    const identifier = extractAndDecodePathSegment(url, urlType);
    if (identifier) {
      return { identifier: identifier.toLowerCase(), type: resultType };
    }
  }

  return { identifier: '', type: null };
}
